import {
    useQuery,
} from "@tanstack/react-query";
import {
    ChevronLeft,
    ChevronRight,
    Info,
    RefreshCw,
} from "lucide-preact";
import {
    useCallback,
    useEffect,
    useState,
} from "preact/compat";

import {
    getPrimaryAnalysis,
} from "@mrflark/spl_sdk";
import type {
    MediaItem,
} from "@mrflark/spl_service_model";
import {
    Button,
    Checkbox,
} from "@mrflark/ui";

import {
    splClient,
} from "../../api/client.ts";
import {
    AiAnalysisDetailModal,
} from "./AiAnalysisDetailModal.tsx";
import {
    AiAnalysisFilterBar,
    DEFAULT_FILTERS,
} from "./AiAnalysisFilterBar.tsx";
import type {
    AiAnalysisFilters,
} from "./AiAnalysisFilterBar.tsx";
import {
    ReanalysisModal,
} from "./ReanalysisModal.tsx";

const generatePageNumbers = (
    current: number,
    total: number,
): (number | string)[] => {
    if (total <= 7) {
        return Array.from({
            length: total,
        }, (_, index) => index + 1);
    }

    const pages: (number | string)[] = [];
    pages.push(1);

    if (current > 3) {
        pages.push("...");
    }

    for (
        let pageNum = Math.max(2, current - 1);
        pageNum <= Math.min(total - 1, current + 1);
        pageNum++
    ) {
        pages.push(pageNum);
    }

    if (current < total - 2) {
        pages.push("...");
    }

    if (total > 1) {
        pages.push(total);
    }

    return pages;
};

const AiItemCard = ({
    item,
    isSelected,
    onToggleSelect,
    onOpenDetails,
}: {
    item: MediaItem;
    isSelected: boolean;
    onToggleSelect: () => void;
    onOpenDetails: () => void;
}) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const analyses = item.ai_analyses;
    const modelKeys = analyses ? Object.keys(analyses) : [];
    const primaryResult = getPrimaryAnalysis(analyses);
    const hasAnalysis = modelKeys.length > 0;
    const confidence = primaryResult?.entry.confidence;

    // prefer the thumbnail (a generated webp from generate_thumbnail)
    // over the source url. for videos this is critical: rendering the
    // raw mp4 in a <video> tag without a poster shows a gray square
    // until the browser loads metadata. the ffmpeg-extracted poster
    // thumbnail renders instantly as an <img>.
    const hasRealThumbnail = Boolean(item.thumbnail_url);
    const thumbnailSrc = item.thumbnail_url ?? item.media_url;

    useEffect(() => {
        setImageUrl(thumbnailSrc ?? null);
    }, [thumbnailSrc]);

    return (
        <div
            data-testid="ai-item-card"
            className={`relative group rounded-lg border overflow-hidden cursor-pointer transition-colors ${
                isSelected
                    ? "border-accent ring-2 ring-accent/30"
                    : "border-border hover:border-text-muted"
            }`}
            onClick={onToggleSelect}
        >
            {/* thumbnail */}
            <div className="aspect-square bg-bg-tertiary">
                {imageUrl
                    ? (
                        item.media_type === "video" && !hasRealThumbnail
                            ? (
                                <video
                                    src={imageUrl}
                                    className="w-full h-full object-cover"
                                    muted
                                    preload="metadata"
                                />
                            )
                            : (
                                <img
                                    src={imageUrl}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            )
                    )
                    : (
                        <div className="w-full h-full flex items-center justify-center text-text-muted text-xs">
                            Loading...
                        </div>
                    )}
            </div>

            {/* model count badge (top-right) */}
            <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
                <span
                    className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${
                        hasAnalysis
                            ? "bg-green-500/90 text-white"
                            : "bg-gray-500/80 text-white"
                    }`}
                >
                    {hasAnalysis
                        ? `${modelKeys.length} model${
                            modelKeys.length !== 1 ? "s" : ""
                        }`
                        : "No analysis"}
                </span>
                {/* details button — opens detail modal without toggling selection */}
                <button
                    type="button"
                    data-testid="ai-item-details"
                    onClick={(event) => {
                        event.stopPropagation();
                        onOpenDetails();
                    }}
                    className="p-1 rounded bg-black/60 text-white hover:bg-black/80 transition-colors"
                    title="View details"
                >
                    <Info size={12} />
                </button>
            </div>

            {/* confidence (bottom-left) */}
            {confidence !== undefined && (
                <div className="absolute bottom-1.5 left-1.5">
                    <span
                        className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                            confidence >= 0.8
                                ? "bg-green-500/90 text-white"
                                : confidence >= 0.5
                                ? "bg-yellow-500/90 text-white"
                                : "bg-red-500/90 text-white"
                        }`}
                    >
                        {(confidence * 100).toFixed(0)}%
                    </span>
                </div>
            )}

            {
                /* checkbox (top-left) — wrapper stops the click from bubbling
                so the card onClick doesn't fire a second toggle */
            }
            <div
                className="absolute top-1.5 left-1.5 bg-white/90 rounded p-0.5"
                onClick={(event) => event.stopPropagation()}
            >
                <Checkbox checked={isSelected} onChange={onToggleSelect} />
            </div>
        </div>
    );
};

export const AiAnalysisTab = () => {
    const [filters, setFilters] = useState<AiAnalysisFilters>(DEFAULT_FILTERS);
    const [page, setPage] = useState(1);
    const perPage = 48;
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [detailItem, setDetailItem] = useState<MediaItem | null>(null);
    const [reanalysisIds, setReanalysisIds] = useState<string[]>([]);
    const [showReanalysisModal, setShowReanalysisModal] = useState(false);
    const [isSelectingAll, setIsSelectingAll] = useState(false);

    useEffect(() => {
        setPage(1);
        setSelectedIds(new Set());
    }, [filters]);

    const {
        data: modelsData,
    } = useQuery({
        queryKey: ["aiModels"],
        queryFn: () => splClient.getAiModels(),
        staleTime: 60000,
    });

    const {
        data,
        isLoading,
    } = useQuery({
        queryKey: ["aiAnalysisItems", filters, page, perPage],
        queryFn: () =>
            splClient.getAiAnalysisItems({
                page,
                perPage,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
                modelName: filters.modelName || undefined,
                minConfidence: filters.minConfidence,
                maxConfidence: filters.maxConfidence,
                analyzedAfter: filters.analyzedAfter || undefined,
                analyzedBefore: filters.analyzedBefore || undefined,
                descriptionSearch: filters.descriptionSearch || undefined,
                hasAnalysis: filters.status === "all"
                    ? undefined
                    : filters.status === "analyzed",
                minAnalysisCount: filters.minAnalysisCount,
                includeVideos: filters.includeVideos,
            }),
        placeholderData: (previousData) => previousData,
    });

    const items = data?.items ?? [];
    const totalItems = data?.total_items ?? 0;
    const totalPages = data?.total_pages ?? 1;

    const toggleSelection = useCallback((itemId: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(itemId)) {
                next.delete(itemId);
            } else {
                next.add(itemId);
            }
            return next;
        });
    }, []);

    const clearSelection = (): void => {
        setSelectedIds(new Set());
    };

    const handleReanalyzeSelected = (): void => {
        setReanalysisIds(Array.from(selectedIds));
        setShowReanalysisModal(true);
    };

    const handleReanalyzeSingle = (item: MediaItem): void => {
        setReanalysisIds([item.id]);
        setShowReanalysisModal(true);
    };

    const handleReanalysisComplete = (): void => {
        clearSelection();
        setReanalysisIds([]);
    };

    const handleSelectAll = (): void => {
        const allVisibleIds = items.map((item) => item.id);
        setSelectedIds((prev) => {
            const allSelected = allVisibleIds.every((id) => prev.has(id));
            if (allSelected) {
                const next = new Set(prev);
                for (const id of allVisibleIds) {
                    next.delete(id);
                }
                return next;
            }
            return new Set([...prev, ...allVisibleIds]);
        });
    };

    const handleSelectAllResults = useCallback(async (): Promise<void> => {
        setIsSelectingAll(true);
        try {
            const response = await splClient.getAiAnalysisIds({
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
                modelName: filters.modelName || undefined,
                minConfidence: filters.minConfidence,
                maxConfidence: filters.maxConfidence,
                analyzedAfter: filters.analyzedAfter || undefined,
                analyzedBefore: filters.analyzedBefore || undefined,
                descriptionSearch: filters.descriptionSearch || undefined,
                hasAnalysis: filters.status === "all"
                    ? undefined
                    : filters.status === "analyzed",
                minAnalysisCount: filters.minAnalysisCount,
            });
            setSelectedIds(new Set(response.ids));
        } catch {
            /* ignore */
        } finally {
            setIsSelectingAll(false);
        }
    }, [filters]);

    const allVisibleSelected = items.length > 0 &&
        items.every((item) => selectedIds.has(item.id));
    const allResultsSelected = totalItems > 0 &&
        selectedIds.size === totalItems;

    return (
        <div className="max-w-6xl mx-auto p-4 space-y-4">
            {/* filter bar */}
            <AiAnalysisFilterBar
                filters={filters}
                onChange={setFilters}
                models={modelsData?.models ?? []}
            />

            {/* summary row */}
            <div className="flex items-center justify-between text-sm text-text-secondary">
                <div className="flex items-center gap-3">
                    <span>
                        {totalItems} item{totalItems !== 1 ? "s" : ""}
                        {items.length > 0 && ` (page ${page} of ${totalPages})`}
                    </span>
                    {items.length > 0 && (
                        <>
                            <button
                                type="button"
                                onClick={handleSelectAll}
                                className="text-xs text-accent hover:text-accent/80 transition-colors"
                            >
                                {allVisibleSelected
                                    ? "Deselect All Visible"
                                    : "Select All Visible"}
                            </button>
                            {totalPages > 1 && (
                                <button
                                    type="button"
                                    onClick={allResultsSelected
                                        ? clearSelection
                                        : handleSelectAllResults}
                                    disabled={isSelectingAll}
                                    className="text-xs text-accent hover:text-accent/80 transition-colors disabled:opacity-50"
                                >
                                    {isSelectingAll
                                        ? "Selecting..."
                                        : allResultsSelected
                                        ? "Deselect All"
                                        : `Select All ${totalItems} Results`}
                                </button>
                            )}
                        </>
                    )}
                </div>
                {selectedIds.size > 0 && (
                    <span className="text-accent">
                        {selectedIds.size} selected
                    </span>
                )}
            </div>

            {/* media grid */}
            {isLoading && items.length === 0
                ? (
                    <div className="p-8 text-center text-text-secondary">
                        Loading...
                    </div>
                )
                : items.length === 0
                ? (
                    <div className="p-12 text-center text-text-secondary bg-bg-secondary rounded-lg border border-border">
                        <p className="text-lg font-medium">No items found</p>
                        <p className="text-sm mt-1">
                            Try adjusting your filters.
                        </p>
                    </div>
                )
                : (
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                        {items.map((item) => (
                            <AiItemCard
                                key={item.id}
                                item={item}
                                isSelected={selectedIds.has(item.id)}
                                onToggleSelect={() => toggleSelection(item.id)}
                                onOpenDetails={() => setDetailItem(item)}
                            />
                        ))}
                    </div>
                )}

            {/* pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-4">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setPage(page - 1)}
                        disabled={page <= 1}
                    >
                        <ChevronLeft size={18} />
                    </Button>

                    <div className="flex items-center gap-1">
                        {generatePageNumbers(page, totalPages).map((
                            pageNum,
                            index,
                        ) => pageNum === "..."
                            ? (
                                <span
                                    key={`ellipsis-${index}`}
                                    className="px-2 text-text-muted"
                                >
                                    ...
                                </span>
                            )
                            : (
                                <button
                                    type="button"
                                    key={pageNum}
                                    onClick={() => setPage(pageNum as number)}
                                    className={`px-3 py-1 rounded transition-colors ${
                                        page === pageNum
                                            ? "bg-accent text-white"
                                            : "bg-bg-tertiary text-text-primary hover:bg-border"
                                    }`}
                                >
                                    {pageNum}
                                </button>
                            )
                        )}
                    </div>

                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setPage(page + 1)}
                        disabled={page >= totalPages}
                    >
                        <ChevronRight size={18} />
                    </Button>
                </div>
            )}

            {/* bulk action bar */}
            {selectedIds.size > 0 && (
                <div className="fixed bottom-0 left-0 right-0 bg-bg-secondary border-t border-border p-4 z-40">
                    <div className="max-w-6xl mx-auto flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-text-primary">
                                {selectedIds.size} selected
                            </span>
                            <button
                                type="button"
                                onClick={clearSelection}
                                className="text-xs text-text-muted hover:text-text-primary transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleReanalyzeSelected}
                        >
                            <RefreshCw size={14} className="mr-1" />
                            Re-analyze
                        </Button>
                    </div>
                </div>
            )}

            {/* detail modal */}
            <AiAnalysisDetailModal
                isOpen={detailItem !== null}
                onClose={() => setDetailItem(null)}
                item={detailItem}
                onReanalyze={handleReanalyzeSingle}
            />

            {/* reanalysis modal */}
            <ReanalysisModal
                isOpen={showReanalysisModal}
                onClose={() => setShowReanalysisModal(false)}
                mediaIds={reanalysisIds}
                onComplete={handleReanalysisComplete}
            />
        </div>
    );
};
